import { useState } from "react";
import InnerPageLayout from "../../components/feature/InnerPageLayout";

const cookieTypes = [
  {
    color: "#A855F7",
    icon: "ri-settings-line",
    name: "필수 쿠키",
    type: "essential",
    required: true,
    desc: "서비스 운영에 반드시 필요한 쿠키입니다. 로그인 상태 유지, 장바구니 정보, 보안 토큰 등을 저장하며 비활성화 시 서비스 이용이 불가합니다.",
    examples: ["세션 쿠키 (로그인 상태)", "CSRF 보안 토큰", "장바구니 임시 저장"],
    duration: "세션 종료 시까지 / 최대 1년",
  },
  {
    color: "#14F1D9",
    icon: "ri-bar-chart-2-line",
    name: "성능 · 분석 쿠키",
    type: "analytics",
    required: false,
    desc: "서비스 이용 현황 및 방문자 행동 패턴을 분석하여 서비스 품질을 개선하기 위한 쿠키입니다. Google Analytics 등 제3자 분석 도구를 사용합니다.",
    examples: ["방문 페이지 및 체류 시간", "유입 경로 분석", "오류 발생 현황"],
    duration: "최대 2년",
  },
  {
    color: "#F97316",
    icon: "ri-settings-3-line",
    name: "기능 쿠키",
    type: "functional",
    required: false,
    desc: "이용자의 언어 설정, 다크모드 선호도 등 맞춤 환경을 기억하기 위한 쿠키입니다. 비활성화 시 매번 설정을 다시 입력해야 할 수 있습니다.",
    examples: ["언어 설정 저장", "테마 설정 기억", "팝업 노출 여부"],
    duration: "최대 1년",
  },
  {
    color: "#C084FC",
    icon: "ri-advertisement-line",
    name: "마케팅 쿠키",
    type: "marketing",
    required: false,
    desc: "이용자의 관심사에 기반한 맞춤 광고를 제공하고 광고 효과를 측정하기 위한 쿠키입니다. 제3자 광고 플랫폼과 공유될 수 있습니다.",
    examples: ["관심사 기반 광고", "리타겟팅 광고", "SNS 픽셀 (Meta, Kakao)"],
    duration: "최대 90일",
  },
];

export default function CookiesPage() {
  const [consent, setConsent] = useState<Record<string, boolean>>({
    essential: true,
    analytics: true,
    functional: true,
    marketing: false,
  });

  const toggle = (type: string) => {
    if (type === "essential") return;
    setConsent((c) => ({ ...c, [type]: !c[type] }));
  };

  return (
    <InnerPageLayout>
      <section className="py-24 lg:py-36 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 lg:mb-16">
            <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-orange mb-5 px-3 py-1 rounded-full border border-brand-orange/30 bg-brand-orange/5">
              Legal
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white leading-snug">쿠키 정책</h1>
            <p className="mt-5 text-white/40 font-body text-sm leading-relaxed">시행일: 2026년 3월 26일 · 최종 수정: 2026년 3월 26일</p>
          </div>

          {/* Intro */}
          <div className="glass rounded-2xl p-6 lg:p-9 mb-10 lg:mb-14">
            <h2 className="font-display text-lg font-bold text-white mb-4">쿠키란 무엇인가요?</h2>
            <p className="text-white/60 text-sm font-body leading-loose">
              쿠키(Cookie)는 웹사이트가 이용자의 브라우저에 저장하는 소규모 텍스트 파일입니다. 쿠키는 이용자가 사이트를 재방문할 때 이전 방문 정보를 기억하거나, 로그인 상태를 유지하거나, 이용자의 선호 설정을 저장하는 등 다양한 용도로 사용됩니다.
              아르트레너는 서비스 품질 향상, 개인화된 경험 제공, 보안 강화를 위해 쿠키를 사용합니다.
            </p>
          </div>

          {/* Cookie types */}
          <div className="flex flex-col gap-5 lg:gap-7 mb-12 lg:mb-16">
            {cookieTypes.map((ct) => (
              <div key={ct.type} className="glass rounded-2xl p-6 lg:p-9 flex flex-col gap-5" style={{ border: `1px solid ${ct.color}20` }}>
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${ct.color}18`, border: `1px solid ${ct.color}30` }}>
                      <i className={`${ct.icon} text-lg`} style={{ color: ct.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-base font-bold text-white">{ct.name}</h3>
                      {ct.required && <span className="text-xs text-white/30 font-body">항상 활성화</span>}
                    </div>
                  </div>
                  {/* Toggle */}
                  <button
                    onClick={() => toggle(ct.type)}
                    className={`relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${ct.required ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    style={{ background: consent[ct.type] ? ct.color : "rgba(255,255,255,0.1)" }}
                    aria-label={`${ct.name} ${consent[ct.type] ? "비활성화" : "활성화"}`}
                  >
                    <span
                      className="absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300"
                      style={{ left: consent[ct.type] ? "calc(100% - 20px)" : "4px" }}
                    />
                  </button>
                </div>

                <p className="text-white/55 text-sm font-body leading-loose">{ct.desc}</p>

                <div className="flex flex-col gap-2">
                  <p className="text-xs font-display font-semibold text-white/30 uppercase tracking-wider">사용 예시</p>
                  <div className="flex flex-wrap gap-2">
                    {ct.examples.map((ex) => (
                      <span key={ex} className="text-xs font-body text-white/50 px-2.5 py-1 rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-white/30 font-body">
                  <span className="font-semibold text-white/40">보존 기간: </span>{ct.duration}
                </p>
              </div>
            ))}
          </div>

          {/* Save button */}
          <div className="text-center mb-12 lg:mb-16">
            <button
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-display font-semibold text-white cursor-pointer transition-all duration-300 whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 20px rgba(168,85,247,0.3)" }}
            >
              <i className="ri-save-line" />
              쿠키 설정 저장하기
            </button>
          </div>

          {/* Additional info */}
          <div className="glass rounded-2xl p-6 lg:p-9 flex flex-col gap-4">
            <h2 className="font-display text-base font-bold text-white">브라우저에서 쿠키 관리하기</h2>
            <p className="text-white/55 text-sm font-body leading-loose">
              대부분의 브라우저는 쿠키를 제어할 수 있는 설정을 제공합니다. 브라우저 설정에서 쿠키를 차단하거나 삭제할 수 있지만, 이 경우 서비스의 일부 기능이 정상적으로 작동하지 않을 수 있습니다.
            </p>
            <div className="flex flex-wrap gap-2 mt-1">
              {["Chrome", "Safari", "Firefox", "Edge"].map((b) => (
                <a key={b} href="#" className="text-xs font-body text-white/50 px-3 py-1.5 rounded-lg glass border border-white/10 hover:border-white/20 hover:text-white/70 cursor-pointer transition-colors whitespace-nowrap">
                  {b} 설정 방법 <i className="ri-external-link-line text-xs" />
                </a>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-white/30 text-xs font-body">쿠키 관련 문의: <a href="mailto:eunhyun1@naver.com" className="text-brand-orange hover:text-brand-orange-bright cursor-pointer transition-colors">eunhyun1@naver.com</a></p>
          </div>
        </div>
      </section>
    </InnerPageLayout>
  );
}
